import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AGENTS = [
  { name: 'lead_research', label: 'Lead Research', color: '#00ff88', icon: '🔍' },
  { name: 'outreach',      label: 'Outreach',       color: '#00cfff', icon: '📨' },
  { name: 'marketing',     label: 'Marketing',      color: '#b57bff', icon: '🎨' },
  { name: 'website_review',label: 'Website Review', color: '#ff9f43', icon: '🌐' },
  { name: 'operations',    label: 'Operations',     color: '#ff6b6b', icon: '⚙️' },
];

const EMPTY_EVOLUTION = {
  approval_rate: 0, reply_rate: 0, conversion_rate: 0,
  actions_count: 0, trust_stage: 1,
  what_changed: '—', new_lessons: [], objections_seen: [], objections_unhandled: [],
};

function TrustRamp({ stage = 1, color }) {
  const stages = ['Probe', 'Listen', 'Draft', 'Refine', 'Send', 'Convert'];
  return (
    <div className="flex gap-1 items-center">
      {stages.map((s, i) => (
        <div key={s} className="flex flex-col items-center gap-1">
          <div
            className="w-8 h-2 rounded-sm transition-all"
            style={{
              background: i < stage ? color : 'rgba(255,255,255,0.08)',
              boxShadow: i < stage ? `0 0 6px ${color}80` : 'none',
            }}
          />
          {i < stage && (
            <span className="text-[8px] font-mono" style={{ color }}>{s}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function MiniChart({ data = [], color, label }) {
  const max = Math.max(...data, 0.01);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-gray-400 font-mono">{label}</span>
      <div className="flex items-end gap-px h-12">
        {data.slice(-14).map((v, i) => (
          <div
            key={i}
            className="flex-1 rounded-sm transition-all"
            style={{
              height: `${Math.max(4, (v / max) * 100)}%`,
              background: `linear-gradient(to top, ${color}cc, ${color}44)`,
              boxShadow: `0 0 4px ${color}66`,
              minWidth: 4,
            }}
          />
        ))}
        {data.length === 0 && (
          <div className="text-[10px] text-gray-600 self-center font-mono">no data yet</div>
        )}
      </div>
      <span className="text-right text-xs font-mono" style={{ color }}>
        {data.length ? `${(data[data.length - 1] * 100).toFixed(1)}%` : '—'}
      </span>
    </div>
  );
}

function StatTile({ label, value, color, unit = '' }) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-1"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${color}33`,
        boxShadow: `inset 0 0 20px ${color}0a`,
      }}
    >
      <span className="text-xs text-gray-400 font-mono uppercase tracking-wider">{label}</span>
      <span className="text-2xl font-bold" style={{ color, fontFamily: 'JetBrains Mono, monospace' }}>
        {value}{unit}
      </span>
    </div>
  );
}

function TerminalLessons({ lessons = [], color }) {
  return (
    <div
      className="rounded-xl p-4 font-mono text-xs"
      style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${color}22` }}
    >
      <div className="mb-2 text-gray-500">// lessons_learned[]</div>
      {lessons.length === 0 && <div className="text-gray-600">{'> no lessons yet'}</div>}
      {lessons.map((l, i) => (
        <div key={i} className="py-0.5" style={{ color }}>
          {'>'} {l}
        </div>
      ))}
    </div>
  );
}

function ObjectionChips({ handled = [], toImprove = [] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {handled.map((o, i) => (
        <span
          key={i}
          className="px-2 py-1 rounded-md text-xs font-mono"
          style={{ background: '#00ff8822', color: '#00ff88', border: '1px solid #00ff8844' }}
        >
          ✓ {o}
        </span>
      ))}
      {toImprove.map((o, i) => (
        <span
          key={i}
          className="px-2 py-1 rounded-md text-xs font-mono"
          style={{ background: '#ffb30022', color: '#ffb300', border: '1px solid #ffb30044' }}
        >
          ⚡ {o}
        </span>
      ))}
      {handled.length === 0 && toImprove.length === 0 && (
        <span className="text-xs text-gray-600 font-mono">no objections tracked yet</span>
      )}
    </div>
  );
}

function AgentCard({ agent, data, evolution }) {
  const { color, label, icon } = agent;
  const today = data?.today || EMPTY_EVOLUTION;
  const evo = evolution || [];

  const approvalSeries = evo.map(e => e.approval_rate || 0);
  const replySeries = evo.map(e => e.reply_rate || 0);
  const conversionSeries = evo.map(e => e.conversion_rate || 0);

  const handled = data?.objections?.handled || [];
  const toImprove = data?.objections?.to_improve || [];
  const memories = data?.memories || [];

  return (
    <div
      className="rounded-2xl p-6 flex flex-col gap-5"
      style={{
        background: 'rgba(10,12,18,0.95)',
        border: `1px solid ${color}33`,
        boxShadow: `0 0 40px ${color}08, inset 0 0 40px rgba(0,0,0,0.2)`,
        animation: 'fadeIn 0.4s ease',
      }}
    >
      {/* Header with tracing-beam border */}
      <div
        className="rounded-xl p-4 flex items-center justify-between"
        style={{
          background: `linear-gradient(135deg, ${color}11, transparent)`,
          border: `1px solid ${color}44`,
          boxShadow: `0 0 0 1px ${color}22`,
        }}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <div className="font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {label} Agent
            </div>
            <div className="text-xs text-gray-400 font-mono">
              {today.actions_count} actions today
            </div>
          </div>
        </div>
        <TrustRamp stage={today.trust_stage || 1} color={color} />
      </div>

      {/* "What changed today" hero card */}
      <div
        className="rounded-xl p-4"
        style={{
          background: '#00ff0808',
          border: '1px solid #00ff8833',
          boxShadow: '0 0 20px #00ff0808',
        }}
      >
        <div className="text-xs font-mono text-green-500 mb-1 uppercase tracking-wider">
          // what changed today that made a difference
        </div>
        <div className="text-sm text-green-300" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {today.what_changed || 'No updates yet — run agents to populate.'}
        </div>
      </div>

      {/* 3 stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Approval Rate" value={((today.approval_rate || 0) * 100).toFixed(0)} color={color} unit="%" />
        <StatTile label="Reply Rate"    value={((today.reply_rate    || 0) * 100).toFixed(0)} color={color} unit="%" />
        <StatTile label="Conversion"    value={((today.conversion_rate|| 0) * 100).toFixed(0)} color={color} unit="%" />
      </div>

      {/* 3 area charts */}
      <div className="grid grid-cols-3 gap-4">
        <MiniChart data={approvalSeries}   color={color} label="Approval / 14d" />
        <MiniChart data={replySeries}      color={color} label="Reply / 14d" />
        <MiniChart data={conversionSeries} color={color} label="Conversion / 14d" />
      </div>

      {/* Lessons learned */}
      <TerminalLessons
        lessons={memories.slice(0, 6).map(m => m.lesson || '')}
        color={color}
      />

      {/* Objections */}
      <div>
        <div className="text-xs text-gray-500 font-mono mb-2 uppercase tracking-wider">
          // objections
        </div>
        <ObjectionChips handled={handled} toImprove={toImprove} />
      </div>
    </div>
  );
}

export default function AgentIntelligence() {
  const { api } = useAuth();
  const [activeAgent, setActiveAgent] = useState(AGENTS[0].name);
  const [agentData, setAgentData] = useState({});
  const [evolution, setEvolution] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAgentData(activeAgent);
  }, [activeAgent]);

  async function loadAgentData(name) {
    setLoading(true);
    try {
      const [todayRes, memRes, objRes, evoRes] = await Promise.all([
        api(`/api/agents/${name}/today`).catch(() => ({ today: null })),
        api(`/api/agents/${name}/memory`).catch(() => ({ memories: [] })),
        api(`/api/agents/${name}/objections`).catch(() => ({ handled: [], to_improve: [] })),
        api(`/api/agents/${name}/evolution?days=30`).catch(() => ({ evolution: [] })),
      ]);
      setAgentData(prev => ({
        ...prev,
        [name]: {
          today: todayRes.today,
          memories: memRes.memories || [],
          objections: { handled: objRes.handled || [], to_improve: objRes.to_improve || [] },
        },
      }));
      setEvolution(prev => ({ ...prev, [name]: evoRes.evolution || [] }));
    } catch (e) {
      console.error('AgentIntelligence load error', e);
    } finally {
      setLoading(false);
    }
  }

  const agent = AGENTS.find(a => a.name === activeAgent);

  return (
    <div
      className="min-h-screen p-6"
      style={{
        background: 'radial-gradient(ellipse at top, #0a0f1e 0%, #050709 100%)',
        fontFamily: 'IBM Plex Sans, sans-serif',
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className="text-2xl font-bold text-white mb-1"
          style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}
        >
          Agent Intelligence
        </h1>
        <p className="text-sm text-gray-500 font-mono">
          Performance memory · decision log · daily evolution · read-only
        </p>
      </div>

      {/* Agent tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {AGENTS.map(a => (
          <button
            key={a.name}
            onClick={() => setActiveAgent(a.name)}
            className="px-4 py-2 rounded-lg text-sm font-mono transition-all"
            style={{
              background: activeAgent === a.name ? `${a.color}22` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${activeAgent === a.name ? a.color : 'rgba(255,255,255,0.08)'}`,
              color: activeAgent === a.name ? a.color : '#9ca3af',
              boxShadow: activeAgent === a.name ? `0 0 12px ${a.color}33` : 'none',
            }}
          >
            {a.icon} {a.label}
          </button>
        ))}
      </div>

      {/* Agent card */}
      {loading ? (
        <div className="text-center py-20 text-gray-600 font-mono">loading intelligence data…</div>
      ) : (
        agent && (
          <AgentCard
            agent={agent}
            data={agentData[activeAgent]}
            evolution={evolution[activeAgent]}
          />
        )
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500&family=JetBrains+Mono:wght@400;500&display=swap');
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
